import React, { useEffect, useMemo, useState } from "react";
import { Search, ShieldCheck, X } from "lucide-react";
import { open_api_with_cookies } from "../../../Utils/network";
import "../shared/AdminData.css";

const PAGE_SIZE = 20;

const userTypeOptions = [
  ["", "All account types"],
  ["1", "Pregnant"],
  ["2", "Parent"],
  ["3", "Expert"],
  ["4", "Admin"],
  ["5", "Event Admin"],
];

const formatDateTime = (value) => {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Never";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const initials = (name) =>
  (name || "U")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

const phoneLabel = (user) => {
  if (!user.phone) return "Not provided";
  return `${user.isd_code || ""} ${user.phone}`.trim();
};

const pageNumbers = (page, totalPages) => {
  const start = Math.max(1, Math.min(page - 2, totalPages - 4));
  return Array.from(
    { length: Math.min(5, totalPages) },
    (_, index) => start + index
  );
};

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [userType, setUserType] = useState("");
  const [installed, setInstalled] = useState("");
  const [activity, setActivity] = useState("");
  const [ordering, setOrdering] = useState("-joined");
  const [selectedUser, setSelectedUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let isCurrent = true;
    const fetchUsers = async () => {
      setIsLoading(true);
      setError("");
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(PAGE_SIZE),
        ordering,
      });
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (userType) params.set("user_type", userType);
      if (installed) params.set("installed", installed);
      if (activity) params.set("activity", activity);

      try {
        const response = await open_api_with_cookies.get(
          `/auth/admin/users/?${params.toString()}`
        );
        if (!isCurrent) return;
        setUsers(response.data.data || []);
        setSummary(response.data.summary || {});
        setTotalPages(response.data.max_pages || 1);
        setTotal(response.data.total_count || 0);
      } catch (requestError) {
        if (!isCurrent) return;
        console.error("Error fetching admin users:", requestError);
        setError("Users could not be loaded. Please try again.");
      } finally {
        if (isCurrent) setIsLoading(false);
      }
    };

    fetchUsers();
    return () => {
      isCurrent = false;
    };
  }, [page, debouncedSearch, userType, installed, activity, ordering]);

  useEffect(() => {
    if (!selectedUser) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setSelectedUser(null);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [selectedUser]);

  const hasFilters = useMemo(
    () => Boolean(search || userType || installed || activity || ordering !== "-joined"),
    [search, userType, installed, activity, ordering]
  );

  const clearFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setUserType("");
    setInstalled("");
    setActivity("");
    setOrdering("-joined");
    setPage(1);
  };

  const summaryCards = [
    ["Total accounts", summary.total_users || 0, "All registered MyBurgo users"],
    ["App installed", summary.app_installed || 0, "Observed app installations"],
    ["Active in 30 days", summary.active_30_days || 0, "Recent mobile app activity"],
    ["AI users", summary.ai_users || 0, "Accounts with an AI session"],
  ];

  return (
    <div className="admin-data-page">
      <div className="admin-data-heading">
        <div>
          <h1>Users</h1>
          <p>Account, contact, installation, app activity, and usage information.</p>
        </div>
        <span className="admin-only-label">
          <ShieldCheck /> Admin only
        </span>
      </div>

      <div className="admin-summary-grid">
        {summaryCards.map(([label, value, note]) => (
          <div className="admin-summary-card" key={label}>
            <div className="admin-summary-label">{label}</div>
            <div className="admin-summary-value">{value.toLocaleString()}</div>
            <div className="admin-summary-note">{note}</div>
          </div>
        ))}
      </div>

      <div className="admin-data-toolbar">
        <label className="admin-data-search">
          <Search aria-hidden="true" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search name, email, phone, username, or ID"
            aria-label="Search users"
          />
        </label>
        <select
          className="admin-filter-select"
          value={userType}
          onChange={(event) => {
            setUserType(event.target.value);
            setPage(1);
          }}
          aria-label="Account type"
        >
          {userTypeOptions.map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <select
          className="admin-filter-select"
          value={installed}
          onChange={(event) => {
            setInstalled(event.target.value);
            setPage(1);
          }}
          aria-label="App installation"
        >
          <option value="">All installations</option>
          <option value="true">App installed</option>
          <option value="false">Not observed</option>
        </select>
        <select
          className="admin-filter-select"
          value={activity}
          onChange={(event) => {
            setActivity(event.target.value);
            setPage(1);
          }}
          aria-label="Recent activity"
        >
          <option value="">All activity</option>
          <option value="7d">Active in 7 days</option>
          <option value="30d">Active in 30 days</option>
          <option value="never">Never used app</option>
        </select>
        <select
          className="admin-filter-select"
          value={ordering}
          onChange={(event) => {
            setOrdering(event.target.value);
            setPage(1);
          }}
          aria-label="Sort users"
        >
          <option value="-joined">Newest joined</option>
          <option value="joined">Oldest joined</option>
          <option value="-last_used">Recently active</option>
          <option value="-last_opened">Recently opened</option>
          <option value="name">Name A–Z</option>
        </select>
        {hasFilters && (
          <button className="admin-clear-filters" onClick={clearFilters}>
            Clear filters
          </button>
        )}
      </div>

      <div className="admin-data-card">
        {isLoading ? (
          <div className="admin-table-loading">Loading users…</div>
        ) : error ? (
          <div className="admin-error-state">{error}</div>
        ) : users.length === 0 ? (
          <div className="admin-empty-state">No users match these filters.</div>
        ) : (
          <div className="admin-table-scroll">
            <table className="admin-data-table admin-users-table">
              <colgroup>
                <col style={{ width: "230px" }} />
                <col style={{ width: "80px" }} />
                <col style={{ width: "150px" }} />
                <col style={{ width: "120px" }} />
                <col style={{ width: "120px" }} />
                <col style={{ width: "155px" }} />
                <col style={{ width: "155px" }} />
                <col style={{ width: "145px" }} />
              </colgroup>
              <thead>
                <tr>
                  <th>User</th>
                  <th>ID</th>
                  <th>Phone</th>
                  <th>Account</th>
                  <th>App</th>
                  <th>Last opened</th>
                  <th>Last used</th>
                  <th>AI activity</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr
                    key={user.id}
                    className="clickable-row"
                    onClick={() => setSelectedUser(user)}
                  >
                    <td>
                      <div className="admin-primary-text">{user.full_name}</div>
                      <div className="admin-secondary-text">
                        {user.email || "No email"}
                      </div>
                    </td>
                    <td><span className="admin-mono-text">#{user.id}</span></td>
                    <td>
                      <div className="admin-primary-text">{phoneLabel(user)}</div>
                    </td>
                    <td>
                      <span className="admin-type-pill">
                        {user.user_type_display || "Unknown"}
                      </span>
                      {user.is_blocked && (
                        <div className="admin-secondary-text">Blocked</div>
                      )}
                    </td>
                    <td>
                      <span
                        className={`admin-status-pill ${
                          user.app_installed ? "status-positive" : "status-neutral"
                        }`}
                      >
                        {user.app_installed ? "Installed" : "Not observed"}
                      </span>
                      <div className="admin-secondary-text">
                        {user.platform_display || "Platform unknown"}
                      </div>
                    </td>
                    <td>{formatDateTime(user.last_app_opened_at)}</td>
                    <td>{formatDateTime(user.last_app_used_at)}</td>
                    <td>
                      <div>{formatDateTime(user.ai_last_activity)}</div>
                      <div className="admin-secondary-text">
                        {user.ai_session_count || 0} sessions · {user.ai_message_count || 0} messages
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!isLoading && !error && total > 0 && (
          <div className="admin-pagination">
            <span>
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString()}
            </span>
            <div className="admin-pagination-controls">
              <button disabled={page === 1} onClick={() => setPage(page - 1)}>
                Previous
              </button>
              {pageNumbers(page, totalPages).map((pageNumber) => (
                <button
                  key={pageNumber}
                  className={pageNumber === page ? "active" : ""}
                  onClick={() => setPage(pageNumber)}
                >
                  {pageNumber}
                </button>
              ))}
              <button
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedUser && (
        <>
          <div
            className="admin-detail-backdrop"
            onClick={() => setSelectedUser(null)}
            aria-hidden="true"
          />
          <aside className="admin-detail-drawer" aria-label="User details">
            <button
              className="admin-drawer-close"
              onClick={() => setSelectedUser(null)}
              aria-label="Close user details"
            >
              <X size={18} />
            </button>
            <div className="admin-user-identity">
              <div className="admin-user-avatar">{initials(selectedUser.full_name)}</div>
              <div>
                <h2>{selectedUser.full_name}</h2>
                <p>#{selectedUser.id} · {selectedUser.user_type_display}</p>
              </div>
            </div>

            <section className="admin-detail-section">
              <h3>Contact & account</h3>
              <div className="admin-detail-row"><span>Email</span><span>{selectedUser.email || "Not provided"}</span></div>
              <div className="admin-detail-row"><span>Phone</span><span>{phoneLabel(selectedUser)}</span></div>
              <div className="admin-detail-row"><span>Username</span><span>{selectedUser.username || "—"}</span></div>
              <div className="admin-detail-row"><span>Joined</span><span>{formatDateTime(selectedUser.date_joined)}</span></div>
              <div className="admin-detail-row"><span>Account status</span><span>{selectedUser.is_blocked ? "Blocked" : selectedUser.is_active ? "Active" : "Inactive"}</span></div>
            </section>

            <section className="admin-detail-section">
              <h3>App activity</h3>
              <div className="admin-detail-row"><span>Installation</span><span>{selectedUser.app_installed ? "Installed" : "Not observed"}</span></div>
              <div className="admin-detail-row"><span>Last opened</span><span>{formatDateTime(selectedUser.last_app_opened_at)}</span></div>
              <div className="admin-detail-row"><span>Last app activity</span><span>{formatDateTime(selectedUser.last_app_used_at)}</span></div>
              <div className="admin-detail-row"><span>Last login</span><span>{formatDateTime(selectedUser.last_login)}</span></div>
              <div className="admin-detail-row"><span>Platform</span><span>{selectedUser.platform_display || "Unknown"}</span></div>
              <div className="admin-detail-row"><span>Device</span><span>{selectedUser.user_device || "Unknown"}</span></div>
              <div className="admin-detail-row"><span>OS / version</span><span>{[selectedUser.user_os, selectedUser.user_version].filter(Boolean).join(" · ") || "Unknown"}</span></div>
            </section>

            <section className="admin-detail-section">
              <h3>Usage</h3>
              <div className="admin-detail-metrics">
                <div className="admin-detail-metric"><strong>{selectedUser.ai_session_count || 0}</strong><span>AI sessions</span></div>
                <div className="admin-detail-metric"><strong>{selectedUser.ai_message_count || 0}</strong><span>AI messages</span></div>
                <div className="admin-detail-metric"><strong>{selectedUser.workshop_purchase_count || 0}</strong><span>Workshops</span></div>
                <div className="admin-detail-metric"><strong>{selectedUser.session_purchase_count || 0}</strong><span>Sessions</span></div>
              </div>
              <div className="admin-detail-row"><span>Last AI activity</span><span>{formatDateTime(selectedUser.ai_last_activity)}</span></div>
            </section>
          </aside>
        </>
      )}
    </div>
  );
};

export default AdminUsers;
